<template>
  <div class="container comprehensive-po-page">
    <div class="title">
      <el-breadcrumb separator="/">
        <el-breadcrumb-item>业务管理</el-breadcrumb-item>
        <el-breadcrumb-item>PO管理</el-breadcrumb-item>
        <el-breadcrumb-item>综合PO工单</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <div class="title-en">搜索条件</div>
    <el-card class="box-card">
      <el-tabs v-model="activeTab" class="po-other-tabs" @tab-click="onTabClick">
        <el-tab-pane v-for="tab in poOtherTabs" :key="tab.name" :label="tab.label" :name="tab.name">
          <span class="po-other-tab-placeholder" />
        </el-tab-pane>
      </el-tabs>
      <div class="search-container">
        <el-form
          :inline="true"
          :model="searchForm"
          class="search-form"
          label-width="100px"
          label-position="right"
        >
          <el-row
            v-for="(row, rowIndex) in searchFieldRows"
            :key="'search-row-' + rowIndex"
            :gutter="20"
            class="search-fields-row"
          >
            <el-col v-for="field in row" :key="field.key" :span="6">
              <el-form-item :label="field.label">
                <el-date-picker
                  v-if="field.type === 'daterange'"
                  v-model="searchForm.createdRange"
                  type="daterange"
                  range-separator="-"
                  style="width: 100%"
                  start-placeholder="开始日期"
                  end-placeholder="结束日期"
                  value-format="yyyy-MM-dd"
                />
                <el-input
                  v-else-if="field.type === 'input'"
                  v-model="searchForm[field.model]"
                  clearable
                  :placeholder="field.placeholder"
                />
                <el-select
                  v-else-if="field.type === 'select'"
                  v-model="searchForm[field.model]"
                  clearable
                  filterable
                  :placeholder="field.placeholder"
                  style="width: 100%"
                  @focus="handleSelectFocus"
                >
                  <el-option
                    v-for="(item, index) in campusOptions"
                    :key="index"
                    :label="Object.values(item)[0]"
                    :value="Object.values(item)[0]"
                  />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="20" class="search-actions-row">
            <el-col :span="6">
              <div class="search-button">
                <el-button type="primary" @click="handleSearch">点击查询</el-button>
                <el-button @click="handleReset">重置</el-button>
                <el-button :loading="exportLoading" @click="debouncedExport">{{ exportButtonText }}</el-button>
              </div>
            </el-col>
          </el-row>
        </el-form>
      </div>
    </el-card>

    <div class="title-en">{{ tableTitle }}</div>
    <el-card class="box-card">
      <div class="table-container">
        <div class="button-container">
          <el-button @click="openColumnsDialog">表头设置</el-button>
        </div>
        <el-table :data="rows" border stripe v-loading="loading" ref="table" @selection-change="handleSelectionChange">
          <el-table-column type="selection" width="55" align="center" />
          <el-table-column type="index" label="序号" width="60" align="center" />
          <el-table-column
            v-for="(column, index) in visibleColumns"
            :key="column.prop + '-' + index"
            :prop="column.prop"
            :label="column.label"
            :width="column.width"
            :min-width="column.minWidth"
            show-overflow-tooltip
          >
            <template slot-scope="scope">
              <span v-if="column.prop === currentTab.ticketProp">
                <a
                  v-if="scope.row[column.prop]"
                  :href="dcopsDetailHref(scope.row[column.prop])"
                  target="_blank"
                  rel="noopener noreferrer"
                >{{ scope.row[column.prop] }}</a>
                <span v-else>-</span>
              </span>
              <span v-else-if="column.prop === 'data'">
                <json-view-dialog
                  :content="scope.row.data"
                  :data-tab="activeTab"
                  :title="pushDialogTitle"
                  width="92%"
                  :max-height="640"
                />
              </span>
              <span v-else-if="column.prop === '_dcopsTicketId' && scope.row._dcopsTicketId">
                <a
                  :href="dcopsDetailHref(scope.row._dcopsTicketId)"
                  target="_blank"
                  rel="noopener noreferrer"
                >{{ scope.row._dcopsTicketId }}</a>
              </span>
              <span v-else-if="isTimeColumn(column.prop)">{{ formatTime(scope.row[column.prop]) }}</span>
              <span v-else>{{ cellText(scope.row[column.prop]) }}</span>
            </template>
          </el-table-column>
        </el-table>

        <el-dialog
          title="设置显示项"
          :visible.sync="showColumnsDialog"
          :append-to-body="true"
          width="400px"
          :close-on-click-modal="false"
          custom-class="columns-dialog"
          :modal-append-to-body="false"
          :destroy-on-close="true"
        >
          <el-checkbox-group v-model="selectedColumns" class="checkbox-group">
            <el-checkbox v-for="column in columns" :key="column.prop" :label="column.prop" class="checkbox-item">
              {{ column.label }}
            </el-checkbox>
          </el-checkbox-group>
          <div slot="footer" class="dialog-footer">
            <el-button size="small" @click="selectAllColumns">全选</el-button>
            <el-button size="small" @click="restoreDefaultColumns">恢复默认</el-button>
            <el-button size="small" type="primary" @click="saveColumnsDialog">保存</el-button>
          </div>
        </el-dialog>

        <div class="pagination-container">
          <el-pagination
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="currentPage"
            :page-sizes="[10, 20, 30, 50]"
            :page-size="pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="total"
          />
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import { PO_OTHER_API_MAP } from '@/api/appManage';
import { getCampusList } from '@/api/operation';
import JsonViewDialog from '@/components/JsonViewDialog';
import { PO_OTHER_TABS, PO_OTHER_TIME_COLS, getPoOtherTab } from '@/constants/poOtherTabs';
import { enrichPoOtherRow, getPushDialogTitle } from '@/utils/poOtherDataView';
import { debounce } from 'lodash';
import * as XLSX from 'xlsx';

function defaultSearchForm() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    createdRange: [
      `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`,
      `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`,
    ],
    op_ticketid: '',
    src_ticket_id: '',
    title: '',
    idcp_name: '',
    scene_name: '',
    review_ticket_id: '',
  };
}

export default {
  name: 'ComprehensivePo',
  components: { JsonViewDialog },
  data() {
    const first = PO_OTHER_TABS[0];
    const defCols = first.columns.map((c) => c.prop);
    return {
      poOtherTabs: PO_OTHER_TABS,
      activeTab: first.name,
      debouncedExport: null,
      rows: [],
      loading: false,
      exportLoading: false,
      currentPage: 1,
      pageSize: 10,
      total: 0,
      columns: [...first.columns],
      defaultSelectedColumns: [...defCols],
      selectedColumns: [...defCols],
      visibleColumns: first.columns.filter((c) => defCols.includes(c.prop)),
      selectedRows: [],
      searchForm: defaultSearchForm(),
      campusOptions: [],
      hasLoaded: false,
      showColumnsDialog: false,
    };
  },
  computed: {
    currentTab() {
      return getPoOtherTab(this.activeTab);
    },
    tableTitle() {
      return this.currentTab.tableTitle || this.currentTab.label;
    },
    ticketLabel() {
      return this.activeTab === 'networkRush' ? '催办工单号' : 'PO单号';
    },
    titleLabel() {
      return this.activeTab === 'networkRush' ? '标题' : '数据说明';
    },
    exportButtonText() {
      return this.exportLoading ? '导出中...' : '导出';
    },
    pushDialogTitle() {
      return getPushDialogTitle(this.activeTab);
    },
    searchFields() {
      const s = this.currentTab.search || {};
      const fields = [
        { key: 'createdRange', label: '时间范围', type: 'daterange' },
        {
          key: 'op_ticketid',
          label: this.ticketLabel,
          type: 'input',
          model: 'op_ticketid',
          placeholder: `请输入${this.ticketLabel}`,
        },
      ];
      if (s.showSrcTicket) {
        fields.push({
          key: 'src_ticket_id',
          label: '关联工单号',
          type: 'input',
          model: 'src_ticket_id',
          placeholder: '请输入关联工单号',
        });
      }
      fields.push({
        key: 'title',
        label: this.titleLabel,
        type: 'input',
        model: 'title',
        placeholder: `请输入${this.titleLabel}`,
      });
      fields.push({
        key: 'idcp_name',
        label: '一级机房',
        type: 'select',
        model: 'idcp_name',
        placeholder: '请选择一级机房',
      });
      if (s.showScene) {
        fields.push({
          key: 'scene_name',
          label: '场景名称',
          type: 'input',
          model: 'scene_name',
          placeholder: '请输入场景名称',
        });
      }
      if (s.showReviewTicket) {
        fields.push({
          key: 'review_ticket_id',
          label: '复核单号',
          type: 'input',
          model: 'review_ticket_id',
          placeholder: '请输入复核单号',
        });
      }
      return fields;
    },
    searchFieldRows() {
      const rows = [];
      const size = 4;
      for (let i = 0; i < this.searchFields.length; i += size) {
        rows.push(this.searchFields.slice(i, i + size));
      }
      return rows;
    },
  },
  created() {
    this.debouncedExport = debounce(this.handleExport, 300);
    this.fetchData();
  },
  methods: {
    apiFn() {
      return PO_OTHER_API_MAP[this.currentTab.api];
    },
    applyTabColumns(tab) {
      this.columns = [...tab.columns];
      this.defaultSelectedColumns = tab.columns.map((c) => c.prop);
      this.selectedColumns = [...this.defaultSelectedColumns];
      this.visibleColumns = tab.columns.filter((c) => this.selectedColumns.includes(c.prop));
    },
    onTabClick() {
      this.applyTabColumns(this.currentTab);
      this.currentPage = 1;
      this.selectedRows = [];
      this.fetchData();
    },
    buildParams(exportExcel) {
      const params = {
        page: this.currentPage,
        size: this.pageSize,
        export_excel: exportExcel ? 1 : undefined,
        op_ticketid: this.searchForm.op_ticketid || undefined,
        src_ticket_id: this.searchForm.src_ticket_id || undefined,
        title: this.searchForm.title || undefined,
        idcp_name: this.searchForm.idcp_name || undefined,
        scene_name: this.searchForm.scene_name || undefined,
      };
      if (this.searchForm.createdRange && this.searchForm.createdRange.length === 2) {
        params.created_start = this.searchForm.createdRange[0];
        params.created_end = this.searchForm.createdRange[1];
      }
      if (this.currentTab.search.showReviewTicket && this.searchForm.review_ticket_id) {
        params.review_ticket_id = this.searchForm.review_ticket_id;
      }
      Object.keys(params).forEach((k) => {
        if (params[k] === '' || params[k] === null || params[k] === undefined) delete params[k];
      });
      if (!exportExcel) delete params.export_excel;
      return params;
    },
    async fetchData() {
      const fn = this.apiFn();
      if (!fn) return;
      this.loading = true;
      try {
        const res = await fn(this.buildParams(false));
        if (res.data && res.data.code === 200) {
          const list = res.data.data || [];
          this.rows = list.map((row) => enrichPoOtherRow(this.activeTab, row));
          this.total = res.data.total ?? 0;
        } else {
          this.rows = [];
          this.total = 0;
          this.$message.error(res.data?.msg || '获取数据失败');
        }
      } catch (e) {
        console.error(e);
        this.$message.error('获取数据失败');
        this.rows = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },
    handleSearch() {
      this.currentPage = 1;
      this.fetchData();
    },
    handleReset() {
      this.searchForm = defaultSearchForm();
      this.currentPage = 1;
      this.fetchData();
    },
    handleSizeChange(val) {
      this.pageSize = val;
      this.currentPage = 1;
      this.fetchData();
    },
    handleCurrentChange(val) {
      this.currentPage = val;
      this.fetchData();
    },
    handleSelectionChange(selection) {
      this.selectedRows = selection;
    },
    openColumnsDialog() {
      this.showColumnsDialog = true;
    },
    saveColumnsDialog() {
      this.showColumnsDialog = false;
      this.visibleColumns = this.columns.filter((c) => this.selectedColumns.includes(c.prop));
    },
    selectAllColumns() {
      this.selectedColumns = this.columns.map((c) => c.prop);
    },
    restoreDefaultColumns() {
      this.selectedColumns = [...this.defaultSelectedColumns];
    },
    async getCampusData() {
      try {
        const response = await getCampusList({ spec_sign: 0 });
        if (response.data && response.data.code === 200) {
          this.campusOptions = response.data.data;
          this.hasLoaded = true;
        }
      } catch (e) {
        console.error('获取园区数据失败:', e);
      }
    },
    handleSelectFocus() {
      if (!this.hasLoaded) this.getCampusData();
    },
    dcopsDetailHref(ticketId) {
      if (!ticketId) return '#';
      return `https://dcops.fre.tencent-cloud.com/appManage/tickets/details?params=${ticketId}`;
    },
    isTimeColumn(prop) {
      return PO_OTHER_TIME_COLS.includes(prop);
    },
    formatTime(val) {
      if (!val) return '-';
      return this.$moment(val).isValid() ? this.$moment(val).format('YYYY-MM-DD HH:mm:ss') : String(val);
    },
    cellText(val) {
      if (val === null || val === undefined || val === '') return '-';
      if (typeof val === 'object') return '[JSON]';
      return String(val);
    },
    exportRowValue(row, column) {
      const val = row[column.prop];
      if (column.prop === 'data') {
        try {
          return typeof val === 'string' ? val : JSON.stringify(val);
        } catch (e) {
          return '-';
        }
      }
      if (this.isTimeColumn(column.prop)) return this.formatTime(val);
      return this.cellText(val);
    },
    async handleExport() {
      this.exportLoading = true;
      try {
        let sourceRows = this.selectedRows;
        if (!sourceRows.length) {
          const res = await this.apiFn()(this.buildParams(true));
          const list = res.data?.data || [];
          sourceRows = list.map((row) => enrichPoOtherRow(this.activeTab, row));
        }
        if (!sourceRows.length) {
          this.$message.warning('暂无数据可导出');
          return;
        }
        const worksheetData = sourceRows.map((row) => {
          const rowData = {};
          this.visibleColumns.forEach((col) => {
            rowData[col.label] = this.exportRowValue(row, col);
          });
          return rowData;
        });
        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, this.currentTab.label);
        const timestamp = this.$moment().format('YYYYMMDDHHmmss');
        XLSX.writeFile(workbook, `综合PO工单_${this.currentTab.label}_${timestamp}.xlsx`);
        this.$message.success('导出成功');
      } catch (e) {
        console.error(e);
        this.$message.error('导出失败');
      } finally {
        this.exportLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.container {
  padding: 20px;
}

.title {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 30px;
}

.title-en {
  margin: 10px 0;
  padding-left: 10px;
  border-left: 3px solid #219aff;
  font-weight: normal;
}

.search-container {
  padding: 20px;
  background-color: #fff;
  border-radius: 4px;
}

.search-form {
  width: 100%;
}

.search-button {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  margin-left: 13px;
  flex-wrap: wrap;
  gap: 8px;
}

.search-form ::v-deep .el-form-item {
  margin-bottom: 18px;
  display: flex;
}

.box-card {
  margin-bottom: 20px;
}

.button-container {
  margin-bottom: 20px;
  display: flex;
  justify-content: flex-end;
  position: relative;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.columns-dialog {
  margin-top: 0 !important;
  position: absolute !important;
  margin-left: 0 !important;
  top: 100% !important;
  left: 0 !important;
  transform: none !important;
}

::v-deep .el-dialog__wrapper.columns-dialog {
  position: absolute !important;
  overflow: visible !important;
}

.checkbox-group {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding: 10px;
  max-height: 300px;
  overflow-y: auto;
}

.checkbox-item {
  margin-right: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dialog-footer {
  text-align: right;
  padding-top: 15px;
  border-top: 1px solid #ebeef5;
}

.dialog-footer .el-button + .el-button {
  margin-left: 8px;
}

.po-other-tabs >>> .el-tabs__header {
  margin-bottom: 0;
  padding: 16px 20px 0;
}

.po-other-tab-placeholder {
  display: none;
}

.po-other-tabs >>> .el-tabs__content {
  display: none;
}

/* 日期、下拉、输入框宽度（与 PO确认单一致） */
::v-deep .el-date-editor.el-input__inner,
::v-deep .el-date-editor--daterange.el-input__inner {
  width: 100% !important;
}

::v-deep .el-range-editor.el-input__inner {
  width: 100% !important;
}

::v-deep .el-date-editor .el-range-input {
  width: 66% !important;
}

::v-deep .el-range-editor--small .el-range-input {
  font-size: 12px !important;
}

.search-form ::v-deep .el-select,
.search-form ::v-deep .el-input {
  width: 100% !important;
}

.search-form ::v-deep .el-form-item__content {
  width: calc(100% - 10px) !important;
}

::v-deep .el-table a {
  color: #409eff;
  text-decoration: none;
  font-weight: 500;
}

::v-deep .el-table a:hover {
  color: #66b1ff;
  text-decoration: underline;
  font-weight: 500;
}
</style>
